import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import FlowScreen from '../components/flow/FlowScreen';
import SplashScreen from '../components/flow/SplashScreen';
import EndPageRenderer from '../components/flow/EndPageRenderer';
import { nextIndex, FLOW_END } from '../lib/flowConditions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AUTO_ADVANCE = new Set(['single_select', 'yes_no', 'rating']);

const getCookie = (n) => { const m = document.cookie.match('(?:^|; )' + n.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'); return m ? decodeURIComponent(m[1]) : null; };
const setCookie = (n, v, maxAge) => { try { document.cookie = `${n}=${encodeURIComponent(v)}; max-age=${maxAge}; path=/; SameSite=Lax`; } catch { /* blocked */ } };

export default function PublicFlow() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const isEmbed = ['1', 'true'].includes(searchParams.get('embed'));
    const ffNext = searchParams.get('ff_next');           // funnel hand-off target (2b-ii)

    const [flow, setFlow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [answers, setAnswers] = useState({});
    const [history, setHistory] = useState([0]);
    const [fieldError, setFieldError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [confirmMessage, setConfirmMessage] = useState('');
    const [endPageStep, setEndPageStep] = useState(null);   // active end_page step (Batch 3)
    const [endPageMeta, setEndPageMeta] = useState({});      // { coach_assigned_user_id, org_id }

    const reachedRef = useRef(new Set());
    const transitionsRef = useRef(0);
    const sessionIdRef = useRef(null);
    const lsKey = `nurtureflow:${slug}`;

    const utm = useMemo(() => {
        const u = {};
        UTM_KEYS.forEach(k => { const v = searchParams.get(k); if (v) u[k] = v; });
        return u;
    }, [searchParams]);

    useEffect(() => {
        axios.get(`${API}/form-flows/public/${slug}`)
            .then(res => setFlow(res.data))
            .catch(err => setError(err.response?.data?.detail || 'Form not found'))
            .finally(() => setLoading(false));
    }, [slug]);

    const screens = useMemo(() => [...(flow?.steps || [])].sort((a, b) => (a.order || 0) - (b.order || 0)), [flow]);

    const branding = flow?.branding || {};
    const buttonColor = branding.button_color || '#1c1917';
    const brandColor = branding.brand_color || buttonColor;
    const index = history[history.length - 1] ?? 0;
    const current = screens[index];
    const currentQuestion = current && current.kind !== 'splash' ? (current.questions || [])[0] : null;

    // ── Persistence (2b-ii): write localStorage + fire-and-forget server save ──
    const persist = useCallback((ans, hist) => {
        const sid = sessionIdRef.current;
        if (!sid) return;
        const reached = Array.from(reachedRef.current);
        try { localStorage.setItem(lsKey, JSON.stringify({ session_id: sid, answers: ans, history: hist, reached, ts: Date.now() })); } catch { /* quota/blocked */ }
        axios.post(`${API}/form-flows/public/${slug}/session`, {
            session_id: sid, answers: ans, step_index: hist[hist.length - 1] ?? 0,
            history: hist, reached_question_ids: reached, utm,
        }).then(r => { if (r.data?.session_id) sessionIdRef.current = r.data.session_id; }).catch(() => {});
    }, [slug, utm, lsKey]);

    // Bootstrap session_id + restore (localStorage instant paint, then server reconcile).
    useEffect(() => {
        if (!flow) return;
        let cached = null;
        try { cached = JSON.parse(localStorage.getItem(lsKey) || 'null'); } catch { /* ignore */ }
        let sid = (cached && cached.session_id) || getCookie(`nf_sid_${slug}`)
            || (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s_${Math.random().toString(36).slice(2)}`);
        sessionIdRef.current = sid;
        setCookie(`nf_sid_${slug}`, sid, 24 * 3600);

        const total = screens.length;
        const clampHist = (h) => { const f = (h || []).filter(i => Number.isInteger(i) && i < total); return f.length ? f : [0]; };
        if (cached && cached.answers) {
            setAnswers(cached.answers);
            (cached.reached || []).forEach(id => reachedRef.current.add(id));
            if (Array.isArray(cached.history)) setHistory(clampHist(cached.history));
        }
        axios.get(`${API}/form-flows/public/${slug}/session/${sid}`).then(res => {
            const d = res.data || {};
            if (d.answers && Object.keys(d.answers).length) {
                setAnswers(d.answers);                          // server is source of truth
                reachedRef.current = new Set(d.reached_question_ids || []);
                setHistory(clampHist(d.history && d.history.length ? d.history : [d.step_index || 0]));
            }
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flow, slug]);

    // Track shown question screens (for reached_question_ids / relaxed required).
    useEffect(() => {
        if (currentQuestion?.question_id) reachedRef.current.add(currentQuestion.question_id);
    }, [currentQuestion]);

    // Early partial-lead capture: when the current email answer becomes valid, save (debounced).
    useEffect(() => {
        if (!currentQuestion || currentQuestion.type !== 'email') return;
        const v = answers[currentQuestion.question_id];
        if (!v || !EMAIL_RE.test(String(v))) return;
        const t = setTimeout(() => persist(answers, history), 800);
        return () => clearTimeout(t);
    }, [answers, currentQuestion, history, persist]);

    // Embed (2b-ii): report content height to the parent for auto-resize.
    useEffect(() => {
        if (!isEmbed) return;
        const send = () => { try { window.parent.postMessage({ type: 'nurture-flow-resize', slug, height: document.body.scrollHeight }, '*'); } catch { /* ignore */ } };
        send();
        const ro = new ResizeObserver(send);
        ro.observe(document.body);
        return () => ro.disconnect();
    }, [isEmbed, slug, submitted, history, endPageStep]);

    const validateOne = (q, val) => {
        if (!q) return null;
        if (q.required && (val === undefined || val === null || val === '' || (Array.isArray(val) && val.length === 0))) return 'This field is required';
        if (q.type === 'email' && val && !EMAIL_RE.test(val)) return 'Please enter a valid email';
        if ((q.type === 'number' || q.type === 'monetary') && val !== undefined && val !== '' && isNaN(Number(val))) return 'Please enter a number';
        return null;
    };

    const doSubmit = useCallback(async (finalAnswers, intoEndPage = false) => {
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/form-flows/public/${slug}/submit`, {
                answers: finalAnswers,
                utm,
                reached_question_ids: Array.from(reachedRef.current),
                session_id: sessionIdRef.current,
                metadata: { referrer: document.referrer, user_agent: navigator.userAgent },
            });
            try { localStorage.removeItem(lsKey); setCookie(`nf_sid_${slug}`, '', 0); } catch { /* ignore */ }
            // Capture coach assignment + org + contact for the end-page coach card
            // (Batch 3; contact_id added Batch 5 so the card can self-assign via round-robin).
            setEndPageMeta({ coach_assigned_user_id: res.data.coach_assigned_user_id || null, org_id: res.data.org_id || null, contact_id: res.data.contact_id || null });
            // When routing into an end_page, that page replaces the generic confirmation
            // and the flow's own redirect — but a funnel hand-off (ff_next) still wins.
            if (ffNext) { window.location.href = ffNext; return; }
            if (intoEndPage) return;
            if (res.data.redirect_url) {
                if (res.data.redirect_new_tab !== false) window.open(res.data.redirect_url, '_blank', 'noopener,noreferrer');
                else { window.location.href = res.data.redirect_url; return; }
            }
            setConfirmMessage(res.data.confirmation_message || 'Thank you!');
            setSubmitted(true);
        } catch (err) {
            setFieldError(err.response?.data?.detail || 'Something went wrong. Please try again.');
        } finally {
            setSubmitting(false);
        }
    }, [slug, utm, ffNext, lsKey]);

    const advance = useCallback((overrideValue) => {
        const step = screens[index];
        if (!step) return;
        let merged = answers;
        if (step.kind !== 'splash') {
            const q = (step.questions || [])[0];
            const val = overrideValue !== undefined ? overrideValue : answers[q?.question_id];
            const err = validateOne(q, val);
            if (err) { setFieldError(err); return; }
            if (overrideValue !== undefined && q) merged = { ...answers, [q.question_id]: overrideValue };
        }
        setFieldError(null);
        transitionsRef.current += 1;
        if (transitionsRef.current > Math.max(screens.length * 3, 30)) { doSubmit(merged); return; }
        const next = nextIndex(screens, index, merged);
        if (next === FLOW_END) { doSubmit(merged); return; }
        // End-page destination (Batch 3): submit + render the end page instead of
        // advancing the question history / showing the generic confirmation.
        if (screens[next]?.kind === 'end_page') {
            setAnswers(merged);
            setEndPageStep(screens[next]);
            doSubmit(merged, true);
            return;
        }
        const newHistory = [...history, next];
        setAnswers(merged);
        setHistory(newHistory);
        persist(merged, newHistory);
    }, [screens, index, history, answers, doSubmit, persist]);

    const goBack = () => { setFieldError(null); setHistory(h => (h.length > 1 ? h.slice(0, -1) : h)); };
    const setAnswer = (qid, val) => setAnswers(a => ({ ...a, [qid]: val }));

    if (loading) return <Center embed={isEmbed} bg={branding.background}><Loader2 className="w-8 h-8 animate-spin text-stone-400" /></Center>;
    if (error) return <Center embed={isEmbed} bg={branding.background}><AlertCircle className="w-10 h-10 text-stone-300 mb-2" /><p className="text-stone-500">{error}</p></Center>;
    if (endPageStep) return (
        <div className={`${isEmbed ? '' : 'min-h-screen'} flex flex-col items-center justify-start px-5 ${isEmbed ? 'py-6' : 'py-10'}`}
            style={{ background: branding.background || '#FAFAF5', fontFamily: `${branding.font || 'Manrope'}, sans-serif` }}>
            <div className="w-full max-w-xl animate-[flowin_220ms_ease-out]">
                {branding.logo && <img src={branding.logo} alt="" className="h-8 mb-6 mx-auto" />}
                <EndPageRenderer endPage={endPageStep.end_page} meta={endPageMeta} brandColor={brandColor} buttonColor={buttonColor} ffNext={ffNext} />
            </div>
            <style>{`@keyframes flowin{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`}</style>
        </div>
    );
    if (submitted) return (
        <Center embed={isEmbed} bg={branding.background}>
            <div className="bg-white rounded-2xl border border-stone-200 p-8 max-w-md text-center shadow-sm">
                <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: brandColor }} />
                <h2 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>All done!</h2>
                <p className="text-sm text-stone-600">{confirmMessage}</p>
            </div>
        </Center>
    );
    if (!current) return <Center embed={isEmbed} bg={branding.background}><p className="text-stone-500">This form has no screens yet.</p></Center>;

    const pct = Math.min(Math.round((history.length / Math.max(screens.length, 1)) * 100), 100);
    const isSplash = current.kind === 'splash';
    const isAuto = !isSplash && currentQuestion && AUTO_ADVANCE.has(currentQuestion.type);

    return (
        <div className={`${isEmbed ? '' : 'min-h-screen'} flex flex-col`} style={{ background: branding.background || '#FAFAF5', fontFamily: `${branding.font || 'Manrope'}, sans-serif` }}>
            <div className="w-full h-1.5 bg-stone-200">
                <div className="h-full transition-all duration-300" style={{ width: `${pct}%`, backgroundColor: brandColor }} />
            </div>

            <div className={`flex-1 flex ${isEmbed ? 'items-start' : 'items-start sm:items-center'} justify-center px-5 ${isEmbed ? 'py-6' : 'py-10'}`}>
                <div key={index} className="w-full max-w-xl animate-[flowin_220ms_ease-out]">
                    {branding.logo && <img src={branding.logo} alt="" className="h-8 mb-6" />}
                    <p className="text-xs font-medium text-stone-400 mb-2">Step {history.length}</p>

                    {isSplash ? (
                        <SplashScreen splash={current.splash} brandColor={brandColor} buttonColor={buttonColor} onContinue={() => advance()} />
                    ) : (
                        <>
                            <h1 className="text-2xl font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                {currentQuestion?.label} {currentQuestion?.required && <span style={{ color: brandColor }}>*</span>}
                            </h1>
                            {currentQuestion?.help_text && <p className="text-sm text-stone-500 mb-5">{currentQuestion.help_text}</p>}
                            <div className="mt-4">
                                <FlowScreen
                                    question={currentQuestion}
                                    value={answers[currentQuestion?.question_id]}
                                    onChange={val => { setFieldError(null); setAnswer(currentQuestion.question_id, val); }}
                                    onAdvance={isAuto ? (v) => advance(v) : undefined}
                                    brandColor={brandColor}
                                    buttonColor={buttonColor}
                                    error={fieldError}
                                />
                            </div>
                            {fieldError && <p className="text-sm text-red-500 mt-2">{fieldError}</p>}
                        </>
                    )}

                    <div className="flex items-center gap-3 mt-7">
                        {history.length > 1 && (
                            <button type="button" onClick={goBack}
                                className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 px-3 py-2.5 min-h-[48px]">
                                <ArrowLeft className="w-4 h-4" /> Back
                            </button>
                        )}
                        {!isSplash && !isAuto && (
                            <button type="button" onClick={() => advance()} disabled={submitting}
                                className="ml-auto px-6 py-3 rounded-xl text-white font-medium text-base min-h-[52px] disabled:opacity-60 flex items-center gap-2"
                                style={{ backgroundColor: buttonColor }}>
                                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                                {nextIndex(screens, index, answers) === FLOW_END ? (submitting ? 'Submitting…' : 'Submit') : 'Continue'}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <style>{`@keyframes flowin{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none}}`}</style>
        </div>
    );
}

function Center({ children, bg, embed }) {
    return <div className={`${embed ? 'min-h-[200px] py-8' : 'min-h-screen'} flex flex-col items-center justify-center px-5`} style={{ background: bg || '#FAFAF5' }}>{children}</div>;
}
